import { ChevronDown, ChevronUp, Tag } from 'react-feather';
import { memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GetEventRegistrationsStatsQuery as GetEventRegistrationsStats } from '__generated__/graphql';

import {
  FilterIndicator, ProgressBar, StatsListContainer, StatsListFooter, StatsListHeader, StatsListLoader, StatsListRow,
  StickyStatsListCard, StickyStatsListHeader,
} from './Layout';
import GetProductStatsQuery from './GetProductStatsQuery';
import SellableLabel from '../../common/Common/SellableLabel';
import UI from '../../common/UI';
import useDashboardUrlState from './useDashboardUrlState';
import useLocale from '../../common/useLocale';
import useMediaDevice from '../../common/useMediaDevice';
import useOnClickOutside from '../../common/useOnClickOutside';
import useQuery from '../../api/useQuery';

type Event = GetEventRegistrationsStats['event'];
type Product = Event['products'][0];

type RevenueColumnOption = 'revenue' | 'remaining';

export interface ProductStatsProps {
  products: Product[];
}

const ProductStats = memo(({ products } : ProductStatsProps) => {
  const { t } = useTranslation();
  const device = useMediaDevice();
  const { formatInteger } = useLocale();

  const limit = 5;
  const [showingAll, setShowingAll] = useState(false);
  const visibleProducts = showingAll ? products : products.slice(0, limit);

  const [revenueColumn, setRevenueColumn] = useState<RevenueColumnOption>('revenue');
  const [showingRevenueColumnOptions, setShowingRevenueColumnOptions] = useState(false);
  const revenueColumnOptionsDropdownRef = useRef();
  useOnClickOutside(revenueColumnOptionsDropdownRef, () => setShowingRevenueColumnOptions(false));

  const hideOptionsAnd = (...callbacks: (() => void)[]) => {
    setShowingRevenueColumnOptions(false);
    callbacks.map((callback) => callback());
  };

  const totals = products.reduce((totals, product) => ({
    sold_count: totals.sold_count + product.sold_count,
    revenue: totals.revenue + product.revenue,
    remaining_amount: totals.remaining_amount + product.remaining_amount,
  }), {
    sold_count: 0,
    revenue: 0,
    remaining_amount: 0,
  });

  return (
    <StickyStatsListCard id="ProductStatsContainer">
      <StickyStatsListHeader>
        <StatsListHeader>
          <UI.FlexContainer sc={{ alignItems: 'center' }}>
            <UI.H4>
              {t('products')}
            </UI.H4>
            <FilterIndicator sc={{ ml: 2 }} />
          </UI.FlexContainer>
          {!device.isSmall && <UI.Div />}
          <UI.Legend sc={{ small: true }}>
            {t('registrations')}
          </UI.Legend>
          {!device.isSmall && (
            <UI.DropdownOptionsContainer ref={revenueColumnOptionsDropdownRef}>
              <UI.Legend sc={{ small: true }}>
                <UI.A
                  role="button"
                  onClick={() => setShowingRevenueColumnOptions((showing) => !showing)}
                >
                  {t(revenueColumn)}
                  <UI.Caret />
                </UI.A>
              </UI.Legend>
              <UI.Dropdown
                sc={{ open: showingRevenueColumnOptions, alignH: 'right', alignV: 'bottom' }}
              >
                <UI.Button
                  onClick={() => hideOptionsAnd(() => setRevenueColumn('revenue'))}
                  sc={{ textAlign: 'left', strong: revenueColumn === 'revenue' }}
                >
                  {t('revenue')}
                </UI.Button>
                <UI.Button
                  onClick={() => hideOptionsAnd(() => setRevenueColumn('remaining'))}
                  sc={{ textAlign: 'left', strong: revenueColumn === 'remaining' }}
                >
                  {t('remaining')}
                </UI.Button>
              </UI.Dropdown>
            </UI.DropdownOptionsContainer>
          )}
        </StatsListHeader>
      </StickyStatsListHeader>
      <StatsListContainer>
        {visibleProducts.map((product) => (
          <ProductRow product={product} key={product.id} revenueColumn={revenueColumn} />
        ))}
        {(showingAll || products.length <= limit) && products.length > 1 && (
          <StatsListRow>
            <UI.Div sc={{ fontWeight: 500 }}>
              {t('totals')}
            </UI.Div>
            {!device.isSmall && <UI.Div />}
            <UI.Div sc={{ noWrap: true }}>
              {formatInteger(totals.sold_count)}
            </UI.Div>
            <RevenueColumn activeColumn={revenueColumn} source={totals} />
          </StatsListRow>
        )}
      </StatsListContainer>
      {products.length > limit && (
        <StatsListFooter>
          {!showingAll && (
            <UI.Button
              onClick={() => setShowingAll(true)}
              sc={{ blank: true, size: 'xs', mr: 2 }}
            >
              <UI.Small>
                {t('show_all')}
                {' '}
                <UI.Span sc={{ muted: true }}>
                  {`(${products.length})`}
                </UI.Span>
                {' '}
                <UI.Icon sc={{ muted: true }}>
                  <ChevronDown />
                </UI.Icon>
              </UI.Small>
            </UI.Button>
          )}
          {showingAll && (
            <UI.Button
              onClick={() => setShowingAll(false)}
              sc={{ blank: true, size: 'xs', mr: 2 }}
            >
              <UI.Small>
                {t('show_less')}
                {' '}
                <UI.Icon sc={{ muted: true }}>
                  <ChevronUp />
                </UI.Icon>
              </UI.Small>
            </UI.Button>
          )}
        </StatsListFooter>
      )}
    </StickyStatsListCard>
  );
});

interface ProductRowProps {
  product: Product;
  revenueColumn: RevenueColumnOption;
}

const ProductRow = ({ product, revenueColumn }: ProductRowProps) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const device = useMediaDevice();

  const hasExtraStats = product.promotions.length > 1 || product.product_variants.length > 1;
  const [showingExtraStats, setShowingExtraStats] = useState(false);

  const toggleExtraStats = () => setShowingExtraStats((showing) => !showing);

  const capacity = product.capacity !== null ? product.capacity : product.indirect_capacity;

  return (
    <>
      <StatsListRow>
        <UI.Div style={{ minWidth: 0 }}>
          <UI.FlexContainer>
            <UI.Truncate>
              <UI.Span sc={{ color: 'gray.800' }}>
                <UI.Icon sc={{ mr: 1 }}>
                  <UI.StatusIndicator sc={{ background: product.active ? 'success' : 'gray.300' }} />
                </UI.Icon>
                {product.title}
              </UI.Span>
            </UI.Truncate>
            {hasExtraStats && (
            <UI.Span>
              <UI.Button
                sc={{ blank: true, size: 'xs', my: -0.75, ml: 1 }}
                onClick={() => toggleExtraStats()}
                aria-label={t('toggle_name', { name: product.title })}
              >
                <UI.Icon>
                  {showingExtraStats && <ChevronUp />}
                  {!showingExtraStats && <ChevronDown />}
                </UI.Icon>
              </UI.Button>
            </UI.Span>
            )}
          </UI.FlexContainer>
        </UI.Div>
        {!device.isSmall && (
          <UI.Div>
            {capacity > 0 && (
              <ProgressBar
                count={product.sold_count}
                capacity={capacity}
                tooltip={t('registrations')}
              />
            )}
          </UI.Div>
        )}
        <UI.Div sc={{ noWrap: true }}>
          {formatInteger(product.sold_count)}
          <UI.Span sc={{ small: true, muted: true }}>
            {' / '}
            {capacity !== null ? formatInteger(capacity) : '∞'}
          </UI.Span>
        </UI.Div>
        <RevenueColumn activeColumn={revenueColumn} source={product} />
      </StatsListRow>
      {showingExtraStats && <ExtraProductStats product={product} revenueColumn={revenueColumn} />}
    </>
  );
};

const ExtraProductStats = ({ product, revenueColumn }: ProductRowProps) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const device = useMediaDevice();

  const [urlState] = useDashboardUrlState();

  const { data } = useQuery(
    GetProductStatsQuery, {
      variables: {
        id: product.id,
        tickets: urlState.tickets || [],
      },
      pollInterval: 30000,
    },
  );

  if (!data) {
    return <StatsListLoader />;
  }

  const { promotions, product_variants: productVariants } = data.product;

  return (
    <>
      {promotions.length > 1 && promotions.map((promotion) => (
        <StatsListRow sc={{ background: 'gray.50' }} key={promotion.id}>
          <UI.Div sc={{ pl: 4 }} style={{ minWidth: 0 }}>
            <UI.Truncate>
              <UI.Icon sc={{ mr: 1 }}>
                <Tag />
              </UI.Icon>
              {promotion.title || t('standard')}
            </UI.Truncate>
          </UI.Div>
          {!device.isSmall && (
            <UI.Div>
              {promotion.capacity > 0 && (
                <ProgressBar
                  count={promotion.sold_count}
                  capacity={promotion.capacity}
                  tooltip={t('registrations')}
                />
              )}
            </UI.Div>
          )}
          <UI.Div sc={{ noWrap: true }}>
            {formatInteger(promotion.sold_count)}
            <UI.Span sc={{ muted: true }}>
              {` / ${promotion.capacity !== null ? formatInteger(promotion.capacity) : '∞'}`}
            </UI.Span>
          </UI.Div>
          <RevenueColumn activeColumn={revenueColumn} source={promotion} />
        </StatsListRow>
      ))}
      {productVariants.length > 1 && productVariants.map((productVariant) => (
        <StatsListRow sc={{ background: 'gray.50' }} key={productVariant.id}>
          <UI.Div sc={{ pl: 4 }} style={{ minWidth: 0 }}>
            <UI.Truncate>
              <SellableLabel type="product">
                {productVariant.title}
              </SellableLabel>
            </UI.Truncate>
          </UI.Div>
          {!device.isSmall && (
            <UI.Div>
              {productVariant.capacity > 0 && (
                <ProgressBar
                  count={productVariant.sold_count}
                  capacity={productVariant.capacity}
                  tooltip={t('registrations')}
                />
              )}
            </UI.Div>
          )}
          <UI.Div sc={{ noWrap: true, pl: [2, 0] }}>
            {formatInteger(productVariant.sold_count)}
            <UI.Span sc={{ muted: true }}>
              {` / ${productVariant.capacity !== null ? formatInteger(productVariant.capacity) : '∞'}`}
            </UI.Span>
          </UI.Div>
          <RevenueColumn activeColumn={revenueColumn} source={productVariant} />
        </StatsListRow>
      ))}
    </>
  );
};

interface RevenueColumnProps {
  activeColumn: RevenueColumnOption;
  source: {
    revenue: number,
    remaining_amount: number,
  };
}

export const RevenueColumn = ({ activeColumn, source }: RevenueColumnProps) => {
  const device = useMediaDevice();
  const { formatCurrency } = useLocale();

  return !device.isSmall && (
    <UI.Div sc={{ noWrap: true, muted: true }}>
      {activeColumn === 'revenue' && formatCurrency(source.revenue)}
      {activeColumn === 'remaining' && formatCurrency(source.remaining_amount)}
    </UI.Div>
  );
};

export default ProductStats;
